import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const ThemeContext = createContext(null);
// Explicit user choice only ("light" | "dark" | "system"). The legacy "theme" key is ignored on purpose: every earlier visitor
// had "light" auto-written, and dark is now the platform default until someone actually picks something.
const STORAGE_KEY = "fixlr_theme";
const DEFAULT_PREFERENCE = "dark";
const PREFERENCES = ["light", "dark", "system"];

const mq = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null);

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_PREFERENCE;
    const stored = localStorage.getItem(STORAGE_KEY);
    return PREFERENCES.includes(stored) ? stored : DEFAULT_PREFERENCE;
  });
  const [systemDark, setSystemDark] = useState(() => !!mq()?.matches);

  useEffect(() => {
    const m = mq();
    if (!m) return undefined;
    const onChange = (e) => setSystemDark(e.matches);
    m.addEventListener ? m.addEventListener("change", onChange) : m.addListener(onChange);
    return () => (m.removeEventListener ? m.removeEventListener("change", onChange) : m.removeListener(onChange));
  }, []);

  const theme = preference === "system" ? (systemDark ? "dark" : "light") : preference;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!PREFERENCES.includes(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setPreferenceState(next);
  }, []);
  const toggleTheme = useCallback(() => setTheme(theme === "dark" ? "light" : "dark"), [theme, setTheme]);

  const value = useMemo(() => ({ theme, preference, setTheme, toggleTheme }), [theme, preference, setTheme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
